import log from "loglevel";
import { useCallback } from "react";

import type { Module } from "ee/constants/ModuleConstants";
import type { Package } from "ee/constants/PackageConstants";

const KEY = "APPSMITH_LAST_VISITED_MODULE_KEY";

type LastVisited = Record<Package["id"], Module | undefined>;
interface LogLastVisitedProps {
  module: Module;
}

interface UseLastVisitedProps {
  basePackageId: Package["id"];
}

export const getValues = () => {
  try {
    const values = localStorage.getItem(KEY) || "{}";

    return JSON.parse(values) as LastVisited;
  } catch (error) {
    log.error(error);

    return undefined;
  }
};

export const setValues = (values: LastVisited) => {
  try {
    // Save to local storage
    localStorage.setItem(KEY, JSON.stringify(values));
  } catch (error) {
    // A more advanced implementation would handle the error case
    log.error(error);
  }
};

export function useLastVisitedModule({ basePackageId }: UseLastVisitedProps) {
  const getLastVisited = useCallback(() => {
    const values = getValues() || {};

    return values[basePackageId];
  }, [getValues, basePackageId]);

  const logLastVisited = useCallback(
    ({ module }: LogLastVisitedProps) => {
      const values = getValues() || {};

      values[basePackageId] = module;
      setValues(values);
    },
    [basePackageId, getValues, setValues],
  );

  return {
    logLastVisited,
    getLastVisited,
  };
}
